import { useEffect, useState } from "react";
import { Search, SlidersHorizontal, MapPin } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import PropertyCard from "@/components/PropertyCard";
import SaveSearchButton from "@/components/SaveSearchButton";
import { api } from "@/lib/api";

const HERO_BG = "https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=1920&q=80";

const ISLANDS = ["all", "Viti Levu", "Vanua Levu", "Mamanuca Islands", "Yasawa Islands", "Kadavu", "Taveuni"];
const TYPES = [
    { value: "all", label: "All listings" },
    { value: "sale", label: "For Sale" },
    { value: "jv", label: "Joint Venture Available" },
];

export default function Marketplace() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [island, setIsland] = useState("all");
    const [type, setType] = useState("all");
    const [maxPrice, setMaxPrice] = useState("");

    const fetchListings = async () => {
        setLoading(true);
        const params = {};
        if (q) params.q = q;
        if (island !== "all") params.island = island;
        if (type !== "all") params.listing_type = type;
        if (maxPrice) params.max_price = parseFloat(maxPrice);
        try {
            const { data } = await api.get("/listings", { params });
            setItems(data.items || []);
        } catch (e) {} finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchListings(); }, []);

    const onSearch = (e) => { e.preventDefault(); fetchListings(); };

    return (
        <div data-testid="marketplace-page">
            <section className="relative h-[44vh] min-h-[320px] flex items-end overflow-hidden">
                <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('${HERO_BG}')` }} />
                <div className="absolute inset-0 hero-overlay" />
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 text-white w-full">
                    <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-3">
                        Fiji <span className="italic text-turquoise">Marketplace</span>
                    </h1>
                    <p className="font-app text-white/85 max-w-2xl">Hand-picked land across Viti Levu, Vanua Levu, the Mamanucas, and the smaller islands.</p>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <form onSubmit={onSearch} className="bg-card border border-border rounded-xl p-4 -mt-12 relative z-10 shadow-xl flex flex-col lg:flex-row items-stretch gap-3">
                    <div className="flex-1 relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input data-testid="marketplace-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by location, region, keyword…" className="pl-9" />
                    </div>
                    <Select value={island} onValueChange={setIsland}>
                        <SelectTrigger data-testid="filter-island" className="lg:w-48"><SelectValue placeholder="Island" /></SelectTrigger>
                        <SelectContent>
                            {ISLANDS.map((i) => <SelectItem key={i} value={i}>{i === "all" ? "All Islands" : i}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Select value={type} onValueChange={setType}>
                        <SelectTrigger data-testid="filter-type" className="lg:w-44"><SelectValue placeholder="Type" /></SelectTrigger>
                        <SelectContent>
                            {TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Input data-testid="filter-max-price" type="number" placeholder="Max FJD" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} className="lg:w-36" />
                    <Button type="submit" data-testid="apply-filters-btn" className="bg-ocean-green text-white hover:opacity-90">
                        <SlidersHorizontal className="h-4 w-4 mr-2" /> Filter
                    </Button>
                </form>

                <div className="mt-8">
                    <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                        <div className="text-sm text-muted-foreground font-app">
                            <MapPin className="inline h-4 w-4 mr-1 text-coral" />
                            {loading ? "Loading…" : `${items.length} listings in Fiji`}
                        </div>
                        <SaveSearchButton
                            kind="listing"
                            filters={{
                                ...(island !== "all" ? { location_island: island } : {}),
                                ...(type !== "all" ? { listing_type: type } : {}),
                                ...(maxPrice ? { max_price: parseFloat(maxPrice) } : {}),
                                ...(q ? { q } : {}),
                            }}
                            defaultName={[
                                island !== "all" ? island : "All Fiji",
                                maxPrice ? `under FJ$${maxPrice}` : null,
                            ].filter(Boolean).join(" · ")}
                        />
                    </div>

                    {!loading && items.length === 0 && (
                        <div className="text-center py-20">
                            <h3 className="font-display text-2xl font-semibold mb-2">No listings match your filters</h3>
                            <p className="text-muted-foreground font-app">Try adjusting the search criteria.</p>
                        </div>
                    )}

                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {items.map((l) => <PropertyCard key={l.id} listing={l} />)}
                    </div>
                </div>
            </section>
        </div>
    );
}
